import type {
  AriaAttributes,
  CSSProperties,
  DOMAttributes,
  MouseEventHandler,
} from 'react';
import type { AlignSelfProps, JustifySelfProps } from 'styled-system';

import type { HTMLTag } from '@/components/Box';
import type { Hidden, Visible } from '@/components/Visibility';
import type { VisibilityProps } from '@/components/Visibility';
import { theme } from '@/theme';
import type { BreakpointsKeys } from '@/theme/types';

import * as S from './Col.styles';

export type ColStart = { [key in `${BreakpointsKeys}Start`]?: number };
export type ColSpan = { [key in BreakpointsKeys]?: number };
export type Order = { [key in `${BreakpointsKeys}Order`]?: number };
export type OrderFirst = { [key in `${BreakpointsKeys}OrderFirst`]?: boolean };
export type OrderLast = { [key in `${BreakpointsKeys}OrderLast`]?: boolean };

export interface ColProps
  extends
    ColSpan,
    ColStart,
    Order,
    OrderFirst,
    OrderLast,
    AlignSelfProps,
    JustifySelfProps,
    Visible,
    Hidden,
    Pick<VisibilityProps, 'handleVisibility' | 'handleDisplay'>,
    DOMAttributes<HTMLElement>,
    AriaAttributes {
  as?: HTMLTag;
  children: React.ReactNode;
  id?: string;
  onClick?: MouseEventHandler;
  role?: string;
  style?: CSSProperties;
  tabIndex?: number;
}

export const Col = ({
  as = 'div',
  children,
  handleDisplay = true,
  handleVisibility = true,
  xs = theme.gridColumns?.xs,
  ...rest
}: ColProps) => {
  return (
    <S.Wrapper
      as={as}
      handleVisibility={handleVisibility}
      xs={xs}
      {...rest}
      data-test-id='ColWrapper'
      handleDisplay={handleDisplay}
    >
      {children}
    </S.Wrapper>
  );
};

Col.displayName = 'Col';
